import { useState } from 'react';
import { Container } from '../../../../types/canvas.types';
import { findContainersInSameNestedRow } from '../utils/containerHelpers';

type ContainerConfigType = {
  id: string;
  name: string;
  width: number;
  widthUnit: '%' | 'px';
  height: number | 'Dynamic';
  heightUnit: 'px';
  alignment: 'left' | 'center' | 'right';
  layout: 'vertical' | 'horizontal';
  showTitle: boolean;
  widthLocked?: boolean;
  collapsible?: boolean;
};

export interface UseContainerWidthReturn {
  constraintMessage: string;
  showConstraintMessage: boolean;
  handleWidthChange: (value: number) => void;
  resetRowWidths: () => void;
  toggleWidthLock: () => void;
  getContainersInSameRow: () => Container[];
}

export const useContainerWidth = (
  selectedContainerId: string | null,
  activePageId: string | null,
  containers: Record<string, Container[]>,
  selectedContainer: Container | null,
  isContainerNested: (containerId: string) => boolean,
  setConfig: React.Dispatch<React.SetStateAction<ContainerConfigType>>,
  setContainers: React.Dispatch<React.SetStateAction<Record<string, Container[]>>>
): UseContainerWidthReturn => {
  const [constraintMessage, setConstraintMessage] = useState('');
  const [showConstraintMessage, setShowConstraintMessage] = useState(false);

  const handleWidthChange = (value: number) => {
    const safeValue = Math.max(25, Math.min(100, value));

    setConfig(prevConfig => ({
      ...prevConfig,
      width: safeValue,
      widthUnit: '%'
    }));

    if (selectedContainerId && activePageId) {
      setContainers(prevContainers => {
        if (!prevContainers[activePageId]) return prevContainers;

        const clonedContainers = [...prevContainers[activePageId]];
        const isNested = selectedContainer?.metadata?.isNested === true;

        if (isNested) {
          const updateNestedContainerWidths = (containers: Container[]): boolean => {
            for (let i = 0; i < containers.length; i++) {
              const container = containers[i];

              if (container.id === selectedContainerId) {
                container.configuration.width = `${safeValue}%`;
                container.configuration.widthLocked = true;
                return true;
              }

              if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                const targetContainer = container.content.nestedContainers.find(c => c.id === selectedContainerId);

                if (targetContainer) {
                  const isFullWidth = targetContainer.configuration.width === '100%';
                  const sameRowContainers = container.content.nestedContainers.filter(nc => {
                    const ncIsFullWidth = nc.configuration.width === '100%';
                    return ncIsFullWidth === isFullWidth;
                  });

                  const lockedContainers = sameRowContainers.filter(
                    c => c.id !== selectedContainerId && c.configuration.widthLocked
                  );

                  const unlockedContainers = sameRowContainers.filter(
                    c => c.id !== selectedContainerId && !c.configuration.widthLocked
                  );

                  const lockedWidth = lockedContainers.reduce((total, c) => {
                    const width = parseFloat(c.configuration.width as string);
                    return total + (isNaN(width) ? 0 : width);
                  }, 0);

                  const remainingWidth = 100 - safeValue - lockedWidth;

                  if (unlockedContainers.length > 0 && remainingWidth < unlockedContainers.length * 25) {
                    const maxWidth = 100 - (lockedWidth + unlockedContainers.length * 25);
                    const constrainedWidth = Math.max(25, Math.min(safeValue, maxWidth));

                    setConstraintMessage(
                      `Width constrained to ${constrainedWidth}% to ensure minimum width for other containers`
                    );
                    setShowConstraintMessage(true);

                    setTimeout(() => {
                      setShowConstraintMessage(false);
                    }, 3000);

                    setConfig(prevConfig => ({
                      ...prevConfig,
                      width: constrainedWidth
                    }));

                    targetContainer.configuration.width = `${constrainedWidth}%`;
                    targetContainer.configuration.widthLocked = true;
                  } else {
                    targetContainer.configuration.width = `${safeValue}%`;
                    targetContainer.configuration.widthLocked = true;

                    if (unlockedContainers.length > 0) {
                      const widthPerUnlocked = Math.max(25, remainingWidth / unlockedContainers.length);

                      unlockedContainers.forEach(c => {
                        c.configuration.width = `${widthPerUnlocked}%`;
                      });
                    }
                  }

                  return true;
                }

                const found = updateNestedContainerWidths(container.content.nestedContainers);
                if (found) return true;
              }
            }

            return false;
          };

          updateNestedContainerWidths(clonedContainers);
        } else {
          const tabContainers = clonedContainers;

          const selectedContainer = tabContainers.find(c => c.id === selectedContainerId);
          if (!selectedContainer) return prevContainers;

          const selectedRowIndex = selectedContainer.metadata?.rowIndex;
          let targetRow: Container[] = [];

          if (selectedRowIndex !== undefined) {
            targetRow = tabContainers.filter(container =>
              !container.metadata?.isNested &&
              container.metadata?.rowIndex === selectedRowIndex
            );
          } else {
            targetRow = [selectedContainer];
          }

          if (targetRow.length === 0) return prevContainers;

          const lockedContainers = targetRow.filter(
            container => container.id !== selectedContainerId && container.configuration.widthLocked
          );

          const unlockedContainers = targetRow.filter(
            container => container.id !== selectedContainerId && !container.configuration.widthLocked
          );

          const lockedWidth = lockedContainers.reduce((total, container) => {
            return total + parseFloat(container.configuration.width as string);
          }, 0);

          const remainingWidth = 100 - safeValue - lockedWidth;

          if (remainingWidth < unlockedContainers.length * 25) {
            const maxWidth = 100 - (lockedWidth + unlockedContainers.length * 25);
            const constrainedWidth = Math.max(25, Math.min(safeValue, maxWidth));

            setConstraintMessage(
              `Width constrained to ${constrainedWidth}% to ensure minimum width for other containers`
            );
            setShowConstraintMessage(true);

            setTimeout(() => {
              setShowConstraintMessage(false);
            }, 3000);

            setConfig(prevConfig => ({
              ...prevConfig,
              width: constrainedWidth,
            }));

            tabContainers.forEach(container => {
              if (container.id === selectedContainerId) {
                container.configuration.width = `${constrainedWidth}%`;
                container.configuration.widthLocked = true;
              }
            });
          } else {
            const widthPerUnlocked = unlockedContainers.length > 0
              ? Math.max(25, remainingWidth / unlockedContainers.length)
              : 0;

            tabContainers.forEach(container => {
              if (container.id === selectedContainerId) {
                container.configuration.width = `${safeValue}%`;
                container.configuration.widthLocked = true;
              }
              else if (targetRow.some(c => c.id === container.id)) {
                if (!container.configuration.widthLocked) {
                  container.configuration.width = `${widthPerUnlocked}%`;
                }
              }
            });
          }
        }

        return {
          ...prevContainers,
          [activePageId]: clonedContainers
        };
      });
    }
  };

  const resetRowWidths = () => {
    if (!selectedContainerId || !activePageId) return;

    const isNested = selectedContainer?.metadata?.isNested === true;

    setContainers(prevContainers => {
      if (!prevContainers[activePageId]) return prevContainers;

      const clonedContainers = [...prevContainers[activePageId]];

      if (isNested) {
        const resetNestedContainerRow = (containers: Container[]): boolean => {
          for (let i = 0; i < containers.length; i++) {
            const container = containers[i];

            if (container.id === selectedContainerId) {
              return false;
            }

            if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
              const targetIndex = container.content.nestedContainers.findIndex(
                c => c.id === selectedContainerId
              );

              if (targetIndex >= 0) {
                const targetContainer = container.content.nestedContainers[targetIndex];

                const isFullWidth = targetContainer.configuration.width === '100%';
                const sameRowContainers = container.content.nestedContainers.filter(nc => {
                  const ncIsFullWidth = nc.configuration.width === '100%';
                  return ncIsFullWidth === isFullWidth;
                });

                const equalWidth = Math.floor(100 / sameRowContainers.length);

                sameRowContainers.forEach(c => {
                  c.configuration.width = `${equalWidth}%`;
                  c.configuration.widthLocked = false;
                });

                setConfig(prevConfig => ({
                  ...prevConfig,
                  width: equalWidth,
                  widthLocked: false
                }));

                return true;
              }

              const found = resetNestedContainerRow(container.content.nestedContainers);
              if (found) return true;
            }
          }

          return false;
        };

        resetNestedContainerRow(clonedContainers);
      } else {
        const tabContainers = clonedContainers;

        const selectedContainer = tabContainers.find(c => c.id === selectedContainerId);
        if (!selectedContainer) return prevContainers;

        const selectedRowIndex = selectedContainer.metadata?.rowIndex;
        let targetRow: Container[] = [];

        if (selectedRowIndex !== undefined) {
          targetRow = tabContainers.filter(container =>
            !container.metadata?.isNested &&
            container.metadata?.rowIndex === selectedRowIndex
          );
        } else {
          targetRow = [selectedContainer];
        }

        if (targetRow.length === 0) return prevContainers;

        const equalWidth = Math.floor(100 / targetRow.length);

        tabContainers.forEach(container => {
          if (targetRow.some(c => c.id === container.id)) {
            container.configuration.width = `${equalWidth}%`;
            container.configuration.widthLocked = false;
          }
        });

        setConfig(prevConfig => ({
          ...prevConfig,
          width: equalWidth,
          widthLocked: false
        }));
      }

      return {
        ...prevContainers,
        [activePageId]: clonedContainers
      };
    });
  };

  const toggleWidthLock = () => {
    if (!selectedContainerId || !activePageId) return;

    const isNested = isContainerNested(selectedContainerId);

    setContainers(prevContainers => {
      if (!prevContainers[activePageId]) return prevContainers;

      const clonedContainers = [...prevContainers[activePageId]];

      if (isNested) {
        const toggleNestedContainerLock = (containers: Container[]): boolean => {
          for (let i = 0; i < containers.length; i++) {
            const container = containers[i];

            if (container.id === selectedContainerId) {
              container.configuration.widthLocked = !container.configuration.widthLocked;
              return true;
            }

            if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
              const targetIndex = container.content.nestedContainers.findIndex(
                c => c.id === selectedContainerId
              );

              if (targetIndex >= 0) {
                const newLockState = !container.content.nestedContainers[targetIndex].configuration.widthLocked;
                container.content.nestedContainers[targetIndex].configuration.widthLocked = newLockState;

                setConfig(prevConfig => ({
                  ...prevConfig,
                  widthLocked: newLockState
                }));

                return true;
              }

              const found = toggleNestedContainerLock(container.content.nestedContainers);
              if (found) return true;
            }
          }

          return false;
        };

        toggleNestedContainerLock(clonedContainers);
      } else {
        clonedContainers.forEach(container => {
          if (container.id === selectedContainerId) {
            const newLockState = !container.configuration.widthLocked;
            container.configuration.widthLocked = newLockState;

            setConfig(prevConfig => ({
              ...prevConfig,
              widthLocked: newLockState
            }));
          }
        });
      }

      return {
        ...prevContainers,
        [activePageId]: clonedContainers
      };
    });
  };

  const getContainersInSameRow = (): Container[] => {
    if (!selectedContainerId || !activePageId || !containers[activePageId]) {
      return [];
    }

    const isNested = isContainerNested(selectedContainerId);

    if (isNested) {
      return findContainersInSameNestedRow(containers[activePageId], selectedContainerId);
    } else {
      const tabContainers = containers[activePageId];
      const selectedContainer = tabContainers.find(c => c.id === selectedContainerId);

      if (!selectedContainer || selectedContainer.metadata?.isNested) {
        return [];
      }

      const selectedRowIndex = selectedContainer.metadata?.rowIndex;

      if (selectedRowIndex !== undefined) {
        return tabContainers.filter(container =>
          !container.metadata?.isNested &&
          container.metadata?.rowIndex === selectedRowIndex
        );
      }

      return [selectedContainer];
    }
  };

  return {
    constraintMessage,
    showConstraintMessage,
    handleWidthChange,
    resetRowWidths,
    toggleWidthLock,
    getContainersInSameRow
  };
};
